import { useCallback } from 'react';

import { useCardApiContext } from 'shared/context/CardApiContext';
import { cardOfferDataSelector, cardOfferSelector, useCardContext } from 'shared/context/CardContext';
import { useCAForCard } from 'shared/hooks/useCAForCard';
import { useRequestAuthentication } from 'shared/hooks/useRequestAuthentication';
import { EDealType } from 'shared/repositories/favorites/entities/schemas/DeleteOfferFromFavoritesRequestSchema';
import { useAddFavoriteMutation } from 'shared/store/serverSide/favorites/v1/add-favorite/mutation';
import { useDeleteOfferFromFavoritesMutation } from 'shared/store/serverSide/favorites/v1/delete-offer-from-favorites/mutation';
import { useIsUserAuthenticatedQuery } from 'shared/store/serverSide/monolith-python/cian-api/site/v1/get-user-data-by-request/query';

export function useToggleFavorite(): VoidFunction {
  const cardContext = useCardContext();

  const cardApi = useCardApiContext();
  const { dealType } = cardContext;
  const {
    extendedProperties: { isFavorite },
  } = cardOfferDataSelector(cardContext);
  const { id: offerId } = cardOfferSelector(cardContext);
  const { data: isUserAuthenticated } = useIsUserAuthenticatedQuery();
  const requestAuthentication = useRequestAuthentication();

  const { mutate: addToFavorite } = useAddFavoriteMutation(cardApi.addToFavorite);
  const { mutate: deleteFromFavorite } = useDeleteOfferFromFavoritesMutation(cardApi.deleteFromFavorite);

  const caForCard = useCAForCard();

  return useCallback<VoidFunction>(() => {
    caForCard(
      { action: isFavorite ? 'remove_from_newlist' : 'add_from_newlist', category: 'favorite' },
      { label: true },
    );

    if (isUserAuthenticated) {
      if (isFavorite) {
        deleteFromFavorite({ dealType: dealType as EDealType, entityId: offerId });
      } else {
        addToFavorite({ dealType: dealType as EDealType, entityId: offerId });
      }
    } else {
      requestAuthentication();
    }
  }, [
    addToFavorite,
    caForCard,
    dealType,
    deleteFromFavorite,
    isFavorite,
    isUserAuthenticated,
    offerId,
    requestAuthentication,
  ]);
}
